import Draggable from "react-draggable";
import { BaseModalHeader } from "./BaseModalHeader.jsx";
import { BaseModalFooter } from "./BaseModalFooter.jsx";
import {
  Dialog,
  DialogPortal
} from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";
import { useEffect, useRef } from "react";

const isEditorInFront = (modalElement, containerElement) => {
  return modalElement === containerElement.lastElementChild;
}

/**
 * @typedef {Object} BaseModalSize
 * @property {string} [minWidth]
 * @property {string} [minHeight]
 * @property {string} [width]
 * @property {string} [height]
 * @property {string} [maxWidth]
 * @property {string} [maxHeight]
 */

/**
 * @typedef {Object} BaseModalProps
 * @property {any} record - The record associated with the modal
 * @property {React.ReactNode} children - Modal content
 * @property {() => void} onClose - Function to call when modal closes
 * @property {string} title - Modal title
 * @property {string} description - Modal description
 * @property {BaseModalSize} [size] - Modal size configuration
 * @property {string} [className] - Additional CSS classes
 * @property {(() => React.ReactNode) | undefined | null} [footerRenderer] - Custom footer renderer function
 * @property {(() => React.ReactNode) | undefined | null} [headerRenderer] - Custom header renderer function
 * @property {string} [headerIconCls] - Header icon class
 * @property {boolean} [modal] - Whether to show overlay
 * @property {Object} [contextMenuConfig] - Context menu configuration
 * @property {any} [version] - Version information
 * @property {any} [swivuAssistanceConfig] - Swivu assistance configuration
 * @property {boolean} [hideMenu] - Whether to hide menu
 * @property {any} editorInstance - Editor instance reference
 * @property {string} [headerClassName] - Additional header CSS classes
 */

/**
 * Base modal component for editors
 * @param {BaseModalProps} props
 */
export const BaseModal = ({
  record,
  children,
  onClose,
  title,
  description,
  size = { minWidth: "300px", minHeight: "300px", width: "300px", height: "300px", maxWidth: "100vw", maxHeight: "100vh" },
  className = "",
  footerRenderer,
  headerRenderer,
  headerIconCls,
  modal,
  contextMenuConfig = {},
  version,
  swivuAssistanceConfig,
  hideMenu = false,
  editorInstance,
  headerClassName = ""
}) => {
  const { minWidth, minHeight, width, height, maxWidth, maxHeight } = size;
  const container = document.querySelector(".b-float-root");
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!editorInstance) throw new Error("editorInstance is required, please provide 'editorInstance' to the BaseModal");

    editorInstance.editorRef = {
      toFront: () => {
        if (modalRef.current && container) {
          const isInFront = isEditorInFront(modalRef.current, container);
          if (isInFront) return;
          const widgetFragment = document.createDocumentFragment();
          if (overlayRef.current) widgetFragment.append(overlayRef.current);
          widgetFragment.append(modalRef.current);
          container.append(widgetFragment);
        }
      }
    }
  }, [editorInstance, modalRef, container]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        const isInFront = isEditorInFront(modalRef.current, container);
        if (isInFront) onClose();
      };
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, modalRef, container])

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal container={container}>
        {modal && <div ref={overlayRef} className="fixed inset-0 bg-black/80 z-[11001] pointer-events-auto" />}
        <div ref={modalRef} className="absolute inset-0 pointer-events-none z-[11001] display flex items-center justify-center">
          <Draggable
            handle=".drag-handle"
            bounds="parent"
            onStart={() => editorInstance.editorRef.toFront()}
          >
            <div
              className={cn(`bg-card text-card-foreground rounded-[var(--swivu-base-border-radius)_var(--swivu-base-border-radius)_2px_2px] shadow-2xl overflow-hidden border border-border border-solid flex flex-col resize pointer-events-auto transition-all duration-100 ease-out`, className)}
              style={{ minWidth, minHeight, width, height, maxWidth, maxHeight }}>
              {headerRenderer ? (
                headerRenderer()
              ) : (
                <BaseModalHeader
                  titleCls={cn(record.archived && "line-through")}
                  title={title}
                  description={description}
                  onClose={onClose}
                  record={record}
                  iconCls={headerIconCls}
                  contextMenuConfig={contextMenuConfig}
                  version={version}
                  swivuAssistanceConfig={swivuAssistanceConfig}
                  hideMenu={hideMenu}
                  onClick={() => editorInstance.editorRef.toFront()}
                  className={headerClassName}
                />
              )}
              {children}
              {footerRenderer && <BaseModalFooter>{footerRenderer()}</BaseModalFooter>}
            </div>
          </Draggable>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
